/** Typisierter API-Client. Authentifizierung laeuft ueber das HttpOnly-Cookie. */

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: 'include',
    headers:
      init.body !== undefined ? { 'Content-Type': 'application/json', ...init.headers } : init.headers,
    ...init,
  });

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const body = payload as ApiErrorBody;
    throw new ApiError(
      response.status,
      body?.error?.code ?? 'ERROR',
      body?.error?.message ?? 'Unbekannter Fehler',
      body?.error?.details,
    );
  }
  return payload as T;
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = (path: string) => request<void>(path, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Typen (Spiegelbild der API-DTOs)
// ---------------------------------------------------------------------------

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'CUSTOMER';
  totpEnabled: boolean;
  createdAt: string;
  locale: 'de' | 'en';
  billingMode: 'PREPAID' | 'INVOICE';
  /** Solange false, ist keine Bestellung moeglich. */
  emailVerified: boolean;
}

// --- Abrechnung -------------------------------------------------------------

export interface Wallet {
  balanceCents: number;
  /** Eingeraeumter Verfuegungsrahmen: so weit darf das Guthaben ins Minus. */
  creditLimitCents: number;
  /** Guthaben plus Rahmen - das ist tatsaechlich verfuegbar. */
  availableCents: number;
  lowBalanceCents: number;
  currency: string;
}

export type LedgerType = 'TOPUP' | 'CHARGE' | 'REFUND' | 'ADJUSTMENT';

export interface LedgerEntry {
  id: string;
  type: LedgerType;
  source: 'MANUAL' | 'PAYPAL' | 'SYSTEM';
  /** Vorzeichenbehaftet: Gutschrift positiv, Abbuchung negativ. */
  amountCents: number;
  balanceAfterCents: number;
  description: string;
  note: string | null;
  createdAt: string;
}

export interface BillingOverview {
  wallet: Wallet;
  billingMode: 'PREPAID' | 'INVOICE';
  /** Summe der monatlichen Kosten aller laufenden Instanzen. */
  monthlyTotalCents: number;
  openInvoices: { count: number; totalCents: number };
  topup: { provider: 'PAYPAL' | null; minCents: number; maxCents: number; currency: string };
}

export interface InvoiceSummary {
  id: string;
  number: string;
  status: 'DRAFT' | 'OPEN' | 'PAID' | 'CANCELLED';
  periodStart: string;
  periodEnd: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  netCents: number;
  taxCents: number;
  grossCents: number;
  currency: string;
}

export interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPriceCents: number;
  totalCents: number;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface InvoiceDetail extends InvoiceSummary {
  taxRatePermille: number;
  address: Record<string, string>;
  items: InvoiceItem[];
}

export interface BillingAddress {
  company: string | null;
  billingStreet: string | null;
  billingZip: string | null;
  billingCity: string | null;
  billingCountry: string;
  vatId: string | null;
  billingMode?: 'PREPAID' | 'INVOICE';
}

export interface BandwidthTier {
  mbps: number;
  priceCents: number;
}

export interface PlanAddon {
  key: string;
  label: string;
  priceCents: number;
}

export interface Plan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  maxPeers: number;
  bandwidthLimitMbps: number;
  ipMode: 'SHARED_IPV4' | 'DEDICATED_IPV4';
  allowSiteToSite: boolean;
  prioritySupport: boolean;
  priceCents: number;
  currency: string;
  bandwidthTiers: BandwidthTier[];
  addons: PlanAddon[];
}

export interface PeerLiveState {
  peerId: string;
  name: string;
  publicKey: string;
  ipv4Address: string;
  ipv6Address: string | null;
  endpoint: string | null;
  lastHandshakeAt: string | null;
  online: boolean;
  rxBytes: number;
  txBytes: number;
  rxBitsPerSecond: number;
  txBitsPerSecond: number;
  enabled: boolean;
}

export interface InstanceSnapshot {
  instanceId: string;
  ifName: string;
  at: string;
  interfaceUp: boolean;
  peerCount: number;
  onlinePeers: number;
  rxBytes: number;
  txBytes: number;
  rxBitsPerSecond: number;
  txBitsPerSecond: number;
  bandwidthLimitMbps: number;
  utilizationPercent: number;
  peers: PeerLiveState[];
}

export interface Instance {
  id: string;
  name: string;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'ERROR' | 'DELETING' | 'DELETED';
  /** Eingefrorener Monatspreis dieser Instanz in Cent. */
  priceCents: number;
  /** Dienst-ID des externen Abrechnungssystems (WHMCS); null = intern abgerechnet. */
  externalRef: string | null;
  /** Papierkorb: gesetzt, solange die Instanz wiederherstellbar ist. */
  deletedAt: string | null;
  deleteAfterAt: string | null;
  statusMessage: string | null;
  ifName: string;
  listenPort: number;
  publicKey: string;
  endpoint: string;
  ipv4Cidr: string;
  ipv4Address: string;
  ipv6Cidr: string | null;
  ipv6Address: string | null;
  ipMode: 'SHARED_IPV4' | 'DEDICATED_IPV4';
  isDedicated: boolean;
  dedicatedIp: string | null;
  maxPeers: number;
  peerCount: number;
  bandwidthLimitMbps: number;
  allowSiteToSite: boolean;
  siteToSiteNets: string[];
  dnsServers: string;
  mtu: number;
  lastSyncAt: string | null;
  createdAt: string;
  plan: {
    id: string;
    slug: string;
    name: string;
    priceCents: number;
    currency: string;
    /** Tarifbasis - Differenz zu maxPeers der Instanz ist die Peer-Zubuchung. */
    maxPeers: number;
    ipMode: 'SHARED_IPV4' | 'DEDICATED_IPV4';
    bandwidthTiers: BandwidthTier[];
    baseBandwidthMbps: number;
  };
  server: { id: string; name: string; location: string | null; status: string };
  live?: InstanceSnapshot | null;
  owner?: { id: string; email: string };
}

export interface Peer {
  id: string;
  name: string;
  publicKey: string;
  hasPresharedKey: boolean;
  ipv4Address: string;
  ipv6Address: string | null;
  extraAllowedIps: string[];
  clientAllowedIps: string;
  persistentKeepalive: number;
  enabled: boolean;
  online: boolean;
  lastHandshakeAt: string | null;
  endpoint: string | null;
  rxBytes: number;
  txBytes: number;
  createdAt: string;
}

export interface TrafficPoint {
  timestamp: string;
  rxBytes: number;
  txBytes: number;
  rxBitsPerSecond: number;
  txBitsPerSecond: number;
  activePeers: number;
}

export interface AdminServer {
  id: string;
  name: string;
  hostname: string;
  location: string | null;
  publicIp: string;
  wanInterface: string;
  ipv4PoolCidr: string;
  listenPortRange: [number, number];
  ifIndexRange: [number, number];
  capacityMbps: number;
  status: string;
  isActive: boolean;
  instanceCount: number;
  publicIpPools: Array<{
    id: string;
    name: string;
    cidr: string;
    gateway: string | null;
    strategy: 'DESCENDING' | 'ASCENDING';
    addressCount: number;
  }>;
  ipv6Pools: Array<{
    id: string;
    name: string;
    cidr: string;
    gateway: string | null;
    allocationCount: number;
  }>;
}

export type IpStatus = 'AVAILABLE' | 'ASSIGNED' | 'RESERVED';

export interface IpamAddress {
  id: string;
  address: string;
  status: IpStatus;
  note: string | null;
  instance: { id: string; name: string; ifName: string; userId: string } | null;
}

export interface IpamIpv4Pool {
  id: string;
  name: string;
  cidr: string;
  gateway: string | null;
  strategy: 'DESCENDING' | 'ASCENDING';
  server: { id: string; name: string; wanInterface: string };
  total: number;
  available: number;
  assigned: number;
  reserved: number;
  addresses: IpamAddress[];
}

export interface IpamIpv6Pool {
  id: string;
  name: string;
  cidr: string;
  gateway: string | null;
  server: { id: string; name: string };
  allocations: Array<{
    id: string;
    cidr: string;
    prefixLength: number;
    status: IpStatus;
    instance: { id: string; name: string; ifName: string } | null;
  }>;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  role: 'ADMIN' | 'CUSTOMER';
  isActive: boolean;
  totpEnabled: boolean;
  instanceCount: number;
  lastLoginAt: string | null;
  createdAt: string;
  locale?: 'de' | 'en';
  emailVerified?: boolean;
}

export interface AdminPlan {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  maxPeers: number;
  bandwidthLimitMbps: number;
  ipMode: 'SHARED_IPV4' | 'DEDICATED_IPV4';
  allowSiteToSite: boolean;
  prioritySupport: boolean;
  priceCents: number;
  currency: string;
  bandwidthTiers: BandwidthTier[];
  isPublic: boolean;
  sortOrder: number;
  instanceCount: number;
}

export interface AdminSetting {
  key: string;
  type: 'string' | 'int' | 'bool' | 'secret';
  choices?: string[];
  /** .env-Rueckfallwert; bei Geheimnissen nur "gesetzt ja/nein". */
  envValue: string | number | boolean;
  overridden: boolean;
  value: string | number | boolean | null;
  secretHint: string | null;
}

export interface AuditEntry {
  id: string;
  action: string;
  target: string | null;
  success: boolean;
  ip: string | null;
  email: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Endpunkte
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    me: () => request<{ user: User }>('/auth/me'),
    login: (email: string, password: string, totpCode?: string) =>
      post<{ user: User }>('/auth/login', { email, password, totpCode }),
    register: (email: string, password: string, name?: string) =>
      post<{ user: User; verificationRequired: boolean }>('/auth/register', { email, password, name }),
    logout: () => post<{ ok: boolean }>('/auth/logout'),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: boolean }>('/auth/change-password', { currentPassword, newPassword }),
    totpSetup: () => post<{ secret: string; otpauthUri: string }>('/auth/totp/setup'),
    totpEnable: (code: string) =>
      post<{ enabled: boolean; recoveryCodes: string[] }>('/auth/totp/enable', { code }),
    totpDisable: (password: string) => post<{ enabled: boolean }>('/auth/totp/disable', { password }),

    /** Antwortet immer gleich - ob das Konto existiert, wird nicht verraten. */
    forgotPassword: (email: string) => post<{ ok: boolean }>('/auth/password/forgot', { email }),
    resetPassword: (token: string, newPassword: string) =>
      post<{ ok: boolean }>('/auth/password/reset', { token, newPassword }),
    verifyEmail: (token: string) => post<{ user: User }>('/auth/verify-email', { token }),
    resendVerification: () =>
      post<{ ok: boolean; alreadyVerified: boolean }>('/auth/verify-email/resend'),
    setLocale: (locale: 'de' | 'en') => patch<{ user: User }>('/auth/locale', { locale }),
  },

  billing: {
    overview: () => request<BillingOverview>('/billing'),
    entries: (limit = 50) => request<{ entries: LedgerEntry[] }>(`/billing/entries?limit=${limit}`),

    /** Legt die Aufladung an und liefert die Weiterleitung zu PayPal. */
    createTopup: (amountCents: number) =>
      post<{ orderId: string; approveUrl: string; amountCents: number }>('/billing/topup/paypal', {
        amountCents,
      }),
    captureTopup: (orderId: string) =>
      post<{ credited: boolean; amountCents: number; balanceCents: number }>(
        `/billing/topup/paypal/${orderId}/capture`,
      ),
    cancelTopup: (orderId: string) =>
      post<{ ok: boolean }>(`/billing/topup/paypal/${orderId}/cancel`),

    invoices: () => request<{ invoices: InvoiceSummary[] }>('/billing/invoices'),
    invoice: (id: string) => request<{ invoice: InvoiceDetail }>(`/billing/invoices/${id}`),

    address: () =>
      request<{ address: BillingAddress; taxRatePermille: number }>('/billing/address'),
    updateAddress: (changes: Partial<BillingAddress>) =>
      patch<{ address: BillingAddress }>('/billing/address', changes),
  },

  adminBilling: {
    wallet: (userId: string) =>
      request<{
        user: { id: string; email: string; billingMode: 'PREPAID' | 'INVOICE' };
        wallet: Wallet;
        entries: LedgerEntry[];
      }>(`/admin/billing/users/${userId}/wallet`),
    /** Manuelle Buchung in beide Richtungen; der Grund ist Pflicht. */
    adjust: (userId: string, amountCents: number, note: string, description?: string) =>
      post<{ wallet: Wallet; entryId: string }>(`/admin/billing/users/${userId}/wallet/adjust`, {
        amountCents,
        note,
        description,
      }),
    setLimits: (userId: string, changes: { creditLimitCents?: number; lowBalanceCents?: number }) =>
      patch<{ wallet: Wallet }>(`/admin/billing/users/${userId}/wallet`, changes),
    setBillingMode: (userId: string, billingMode: 'PREPAID' | 'INVOICE') =>
      patch<{ user: { id: string; email: string; billingMode: string } }>(
        `/admin/billing/users/${userId}/billing-mode`,
        { billingMode },
      ),

    invoices: (status?: InvoiceSummary['status']) =>
      request<{
        invoices: Array<
          Pick<InvoiceSummary, 'id' | 'number' | 'status' | 'grossCents' | 'currency' | 'issuedAt' | 'dueAt' | 'paidAt'> & {
            user: { id: string; email: string; name: string | null };
          }
        >;
      }>(`/admin/billing/invoices${status ? `?status=${status}` : ''}`),
    markPaid: (id: string) =>
      post<{ invoice: { id: string; number: string; status: string } }>(
        `/admin/billing/invoices/${id}/paid`,
      ),
    issue: (id: string) => post<{ number: string }>(`/admin/billing/invoices/${id}/issue`),
  },

  plans: {
    list: () => request<{ plans: Plan[] }>('/plans'),
  },

  instances: {
    list: () => request<{ instances: Instance[] }>('/instances'),
    get: (id: string) =>
      request<{ instance: Instance; peers: Peer[]; live: InstanceSnapshot | null }>(`/instances/${id}`),
    create: (input: {
      planId: string;
      name: string;
      bandwidthMbps?: number;
      /** Zubuchung: eigene öffentliche IPv4 statt geteiltem NAT. */
      dedicatedIpv4?: boolean;
    }) => post<{ instance: Instance }>('/instances', input),
    update: (
      id: string,
      changes: {
        name?: string;
        dnsServers?: string;
        mtu?: number;
        endpointHost?: string | null;
        siteToSiteNets?: string[];
      },
    ) => patch<{ instance: Instance }>(`/instances/${id}`, changes),
    setBandwidth: (id: string, mbps: number) =>
      post<{ instance: Instance }>(`/instances/${id}/bandwidth`, { mbps }),
    remove: (id: string) => del(`/instances/${id}`),
    status: (id: string) => request<{ live: InstanceSnapshot | null }>(`/instances/${id}/status`),
    stats: (id: string, hours = 24) =>
      request<{ stats: TrafficPoint[] }>(`/instances/${id}/stats?hours=${hours}`),

    peers: {
      list: (instanceId: string) =>
        request<{ peers: Peer[]; limits: { maxPeers: number; used: number } }>(
          `/instances/${instanceId}/peers`,
        ),
      create: (
        instanceId: string,
        input: {
          name: string;
          publicKey?: string;
          usePresharedKey?: boolean;
          extraAllowedIps?: string[];
          clientAllowedIps?: string;
          persistentKeepalive?: number;
        },
      ) => post<{ peer: Peer; config: string | null }>(`/instances/${instanceId}/peers`, input),

      /**
       * Erzeugt ein neues Schlüsselpaar und liefert die Konfiguration einmalig.
       * Der private Schlüssel wird serverseitig nie gespeichert.
       */
      rotate: (instanceId: string, peerId: string) =>
        post<{ peer: Peer; config: string }>(`/instances/${instanceId}/peers/${peerId}/rotate`),

      /**
       * Konfiguration eines bestehenden Peers erneut abrufen. Der private
       * Schluessel ist serverseitig nicht gespeichert und fehlt deshalb -
       * `includesPrivateKey` ist immer false.
       */
      config: (instanceId: string, peerId: string) =>
        request<{ config: string; includesPrivateKey: boolean; filename: string }>(
          `/instances/${instanceId}/peers/${peerId}/config`,
        ),
      update: (
        instanceId: string,
        peerId: string,
        changes: {
          name?: string;
          enabled?: boolean;
          extraAllowedIps?: string[];
          clientAllowedIps?: string;
          persistentKeepalive?: number;
        },
      ) => patch<{ peer: Peer }>(`/instances/${instanceId}/peers/${peerId}`, changes),
      remove: (instanceId: string, peerId: string) => del(`/instances/${instanceId}/peers/${peerId}`),

      /** Rendert einen QR-Code zu einer soeben erzeugten Konfiguration. */
      qrCode: (instanceId: string, peerId: string, config: string) =>
        fetch(`/api/instances/${instanceId}/peers/${peerId}/qrcode`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config }),
        }).then((response) => {
          if (!response.ok) throw new ApiError(response.status, 'ERROR', 'QR-Code nicht abrufbar');
          return response.text();
        }),
    },
  },

  monitor: {
    overview: () =>
      request<{
        totals: {
          instances: number;
          active: number;
          onlinePeers: number;
          peers: number;
          rxBitsPerSecond: number;
          txBitsPerSecond: number;
        };
        instances: Array<{
          id: string;
          name: string;
          ifName: string;
          status: string;
          bandwidthLimitMbps: number;
          maxPeers: number;
          live: InstanceSnapshot | null;
        }>;
      }>('/monitor/overview'),
  },

  admin: {
    servers: () => request<{ servers: AdminServer[] }>('/admin/servers'),
    createServer: (input: Record<string, unknown>) => post<unknown>('/admin/servers', input),
    updateServer: (id: string, changes: Record<string, unknown>) =>
      patch<unknown>(`/admin/servers/${id}`, changes),
    deleteServer: (id: string) => del(`/admin/servers/${id}`),
    ipam: () => request<{ ipv4Pools: IpamIpv4Pool[]; ipv6Pools: IpamIpv6Pool[] }>('/admin/ipam'),
    createIpv4Pool: (
      serverId: string,
      input: { cidr: string; gateway?: string; strategy?: 'DESCENDING' | 'ASCENDING'; name?: string },
    ) => post<{ pool: unknown; added: number }>(`/admin/servers/${serverId}/ipv4-pools`, input),
    createIpv6Pool: (serverId: string, input: { cidr: string; gateway?: string; name?: string }) =>
      post<{ pool: unknown }>(`/admin/servers/${serverId}/ipv6-pools`, input),
    addAddresses: (poolId: string, addresses: string[], note?: string) =>
      post<{ added: number; skipped: number }>(`/admin/ipv4-pools/${poolId}/addresses`, {
        addresses,
        note,
      }),
    setAddressStatus: (id: string, status: 'AVAILABLE' | 'RESERVED') =>
      patch<{ address: IpamAddress }>(`/admin/ipv4-addresses/${id}`, { status }),
    deleteAddress: (id: string) => del(`/admin/ipv4-addresses/${id}`),

    users: () => request<{ users: AdminUser[] }>('/admin/users'),
    createUser: (input: { email: string; password: string; name?: string; role: 'ADMIN' | 'CUSTOMER' }) =>
      post<unknown>('/admin/users', input),
    updateUser: (
      id: string,
      changes: {
        role?: 'ADMIN' | 'CUSTOMER';
        isActive?: boolean;
        password?: string;
        name?: string | null;
        email?: string;
        locale?: 'de' | 'en';
        emailVerified?: boolean;
      },
    ) => patch<unknown>(`/admin/users/${id}`, changes),
    /** Setzt die Zwei-Faktor-Anmeldung zurueck und beendet alle Sitzungen. */
    resetTotp: (id: string) => post<unknown>(`/admin/users/${id}/totp-reset`),
    /** Bestaetigungslink erneut verschicken (frisches Token). */
    sendVerification: (id: string) =>
      post<{ ok: boolean; to: string }>(`/admin/users/${id}/send-verification`),

    mailLog: (status?: 'SENT' | 'FAILED' | 'DISABLED') =>
      request<{
        entries: Array<{
          id: string;
          email: string;
          kind: string;
          subject: string;
          status: 'SENT' | 'FAILED' | 'DISABLED';
          error: string | null;
          createdAt: string;
        }>;
      }>(`/admin/mail-log${status ? `?status=${status}` : ''}`),
    /** Loescht ein Konto - nur ohne Instanzen, nie das eigene. */
    deleteUser: (id: string) => del(`/admin/users/${id}`),

    plans: () => request<{ plans: AdminPlan[] }>('/admin/plans'),
    createPlan: (
      input: Partial<Omit<AdminPlan, 'id' | 'instanceCount'>> &
        Pick<AdminPlan, 'slug' | 'name' | 'maxPeers' | 'bandwidthLimitMbps' | 'ipMode' | 'allowSiteToSite' | 'priceCents'>,
    ) => post<{ plan: unknown }>('/admin/plans', input),
    updatePlan: (id: string, changes: Partial<Omit<AdminPlan, 'id' | 'instanceCount'>>) =>
      patch<{ plan: unknown }>(`/admin/plans/${id}`, changes),
    deletePlan: (id: string) => del(`/admin/plans/${id}`),

    settings: () => request<{ settings: AdminSetting[] }>('/admin/settings'),
    saveSettings: (changes: Record<string, string | number | boolean | null>) =>
      request<{ settings: AdminSetting[] }>('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({ changes }),
      }),
    testMail: () => post<{ ok: boolean; to: string }>('/admin/settings/test-mail'),
    testPaypal: () => post<{ ok: boolean; mode: string; message?: string }>('/admin/settings/test-paypal'),

    instances: () => request<{ instances: Instance[] }>('/admin/instances'),
    suspend: (id: string, reason?: string) => post<unknown>(`/instances/${id}/suspend`, { reason }),
    resume: (id: string) => post<unknown>(`/instances/${id}/resume`),
    /** Holt eine vom Kunden geloeschte Instanz aus dem Papierkorb zurueck. */
    restore: (id: string) => post<{ instance: Instance }>(`/admin/instances/${id}/restore`),
    /** Loescht eine Papierkorb-Instanz sofort endgueltig. */
    purge: (id: string) => del(`/admin/instances/${id}/purge`),

    mailTemplates: () =>
      request<{
        mailEnabled: boolean;
        templates: Array<{
          kind: string;
          placeholders: string[];
          defaults: Record<'de' | 'en', { subject: string; body: string }>;
          overrides: Partial<Record<'de' | 'en', { subject?: string; body?: string }>>;
        }>;
      }>('/admin/mail-templates'),
    saveMailTemplate: (
      kind: string,
      locale: 'de' | 'en',
      changes: { subject?: string | null; body?: string | null },
    ) => request<unknown>(`/admin/mail-templates/${kind}`, {
      method: 'PUT',
      body: JSON.stringify({ locale, ...changes }),
    }),
    testMailTemplate: (kind: string, locale: 'de' | 'en') =>
      post<{ ok: boolean; to: string }>(`/admin/mail-templates/${kind}/test`, { locale }),
    reconcile: () => post<{ restored: number; failed: number }>('/admin/reconcile'),
    audit: (take = 100) => request<{ entries: AuditEntry[] }>(`/admin/audit?take=${take}`),
  },
};
